import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { PREFERRED_UNIT_SYSTEMS, PreferredUnitSystem } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  HStack,
  Text,
  GearIcon,
  Segment,
  SegmentControl,
  Switch,
} from '@frontend/legacy-components';

import { cadViewer } from 'modules/common/viewer/cadViewer/data/actions';
import {
  getDimensionalPrecision,
  getRenderNativeColors,
} from 'modules/common/viewer/cadViewer/data/reducer';
import { ViewerPopoverButton } from 'modules/common/viewer/components';
import { viewer } from 'modules/common/viewer/data/actions';
import {
  getActivePartFileUuid,
  getIsLegacyDxfViewerForPartFile,
} from 'modules/common/viewer/data/reducer';
import { useIsActiveFileDxfSceneEligible } from 'modules/common/viewer/hooks/useActiveViewerKind';
import {
  useViewerUnitSystemPreference,
  inferDisplayUnits,
} from 'modules/common/viewer/hooks/useViewerUnitSystemPreference';

import { useAppSelector } from 'store/hooks';

import { useUnitSystemContext } from 'utils/measurement';

const UNIT_SYSTEM_PREFERENCE_KEY = 'cadViewer:unitSystemPreference';

const CADViewerDisplayOptionsPopover = ({
  showDxfControls,
}: {
  showDxfControls: boolean;
}) => {
  const {
    preferredUnitSystem: groupPreferredUnitSystem,
    setPreferredUnitSystem: setGroupPreferredUnitSystem,
  } = useUnitSystemContext();
  const dimensionalPrecision = useAppSelector(getDimensionalPrecision);
  const renderNativeColors = useAppSelector(getRenderNativeColors);
  const activePartFileUuid = useAppSelector(getActivePartFileUuid);
  const isLegacyDxfViewer = useAppSelector((state) =>
    getIsLegacyDxfViewerForPartFile(state, activePartFileUuid),
  );
  const isDxfSceneEligible = useIsActiveFileDxfSceneEligible();
  const showLegacyDxfViewerToggle = showDxfControls && isDxfSceneEligible;
  const { preferredUnitSystem, setPreferredUnitSystem } =
    useViewerUnitSystemPreference({
      preferredUnitSystem: groupPreferredUnitSystem,
      setPreferredUnitSystem: setGroupPreferredUnitSystem,
      storageKey: UNIT_SYSTEM_PREFERENCE_KEY,
    });

  const dispatch = useDispatch();
  const receiveDisplayUnits = useCallback(
    (units: { displayUnits: 'in' | 'mm' }) => {
      dispatch(cadViewer.receiveDisplayUnits(units));
    },
    [dispatch],
  );
  const receiveDimensionalPrecision = useCallback(
    (precision: { dimensionalPrecision: number }) => {
      dispatch(cadViewer.receiveDimensionalPrecision(precision));
    },
    [dispatch],
  );
  const setRenderNativeColors = useCallback(
    (colors: { renderNativeColors: boolean }) => {
      dispatch(cadViewer.setRenderNativeColors(colors));
    },
    [dispatch],
  );

  const handleUnitsChange = (value: PreferredUnitSystem) => {
    if (value !== preferredUnitSystem) {
      receiveDisplayUnits({
        displayUnits: inferDisplayUnits(value),
      });
      setPreferredUnitSystem(value);
    }
    if (value === PREFERRED_UNIT_SYSTEMS.METRIC && dimensionalPrecision > 4) {
      receiveDimensionalPrecision({ dimensionalPrecision: 4 });
    }
  };

  const handleColorsChange = (value: string | boolean) => {
    const next = value === 'true' || value === true;
    if (next !== renderNativeColors) {
      setRenderNativeColors({ renderNativeColors: next });
    }
  };

  const handlePrecisionChange = (valueAsString: string | number) => {
    const n =
      typeof valueAsString === 'number' ? valueAsString : Number(valueAsString);
    if (!Number.isFinite(n) || n === dimensionalPrecision) {
      return;
    }
    receiveDimensionalPrecision({ dimensionalPrecision: n });
  };

  const setIsLegacyDxfViewer = (nextIsLegacyDxfViewer: boolean) => {
    if (!activePartFileUuid) {
      return;
    }
    dispatch(
      viewer.setLegacyDxfViewerForPartFile({
        partFileUuid: activePartFileUuid,
        isLegacyDxfViewer: nextIsLegacyDxfViewer,
      }),
    );
  };

  return (
    <ViewerPopoverButton
      title="Display options"
      icon={<GearIcon boxSize={3} />}
    >
      <>
        <FormControl mb={1.5}>
          <FormLabel>Units</FormLabel>
          <SegmentControl
            name="cad-viewer-display-units"
            size="sm"
            value={preferredUnitSystem}
            onChange={(value) =>
              handleUnitsChange(value as PreferredUnitSystem)
            }
            w="100%"
          >
            <Segment value={PREFERRED_UNIT_SYSTEMS.IMPERIAL}>Imperial</Segment>
            <Segment value={PREFERRED_UNIT_SYSTEMS.METRIC}>Metric</Segment>
          </SegmentControl>
        </FormControl>
        <FormControl mb={1.5}>
          <FormLabel>Colors</FormLabel>
          <SegmentControl
            name="cad-viewer-display-colors"
            size="sm"
            value={renderNativeColors ? 'true' : 'false'}
            onChange={(value) => handleColorsChange(value as string | boolean)}
            w="100%"
          >
            <Segment value="false">Model</Segment>
            <Segment value="true">Default</Segment>
          </SegmentControl>
        </FormControl>
        <FormControl mb={1.5}>
          <FormLabel>Dimensional precision</FormLabel>
          <HStack>
            <Text fontSize="sm">Decimal places</Text>
            <NumberInput
              clampValueOnBlur
              focusInputOnChange
              max={
                preferredUnitSystem === PREFERRED_UNIT_SYSTEMS.METRIC ? 4 : 6
              }
              min={1}
              step={1}
              value={dimensionalPrecision}
              onChange={handlePrecisionChange}
              pattern="[0-9]*(.[0-9]+)?"
              size="sm"
              width="auto"
            >
              <NumberInputField width={6} />
            </NumberInput>
          </HStack>
        </FormControl>
        {showLegacyDxfViewerToggle && (
          <FormControl mb={1.5}>
            <FormLabel>Legacy DXF Viewer</FormLabel>
            <HStack>
              <Text fontSize="sm">Legacy DXF Viewer</Text>
              <Switch
                name="cad-viewer-legacy-viewer-toggle"
                size="sm"
                isChecked={isLegacyDxfViewer}
                onChange={(event) => setIsLegacyDxfViewer(event.target.checked)}
              />
            </HStack>
          </FormControl>
        )}
      </>
    </ViewerPopoverButton>
  );
};

CADViewerDisplayOptionsPopover.displayName = 'CADViewerDisplayOptionsPopover';
export default CADViewerDisplayOptionsPopover;
